import {
  AnalyticsInputMessagePayload,
  AnalyticsInputRecord,
  AnalyticsInputTypesRegistry,
  AnonymousAppUserEvent,
} from '@ilteducation/analytics-gateway-types';
import { v4 as uuidv4 } from 'uuid';
import { useConfigHelper } from '@ilteducation/config';
import axios, { AxiosRequestConfig } from 'axios';

const { getApiUrl } = useConfigHelper();

const buildRequestBody = <T extends keyof AnalyticsInputTypesRegistry>(
  eventType: T,
  data: AnalyticsInputTypesRegistry[T],
): AnalyticsInputMessagePayload => {
  const record: AnalyticsInputRecord = {
    eventType,
    metadata: {
      eventUID: uuidv4(),
      timestamp: new Date().toISOString(),
    },
    data,
  };

  return { records: [record] };
};

export const postAnalyticsEvent = async <T extends keyof AnalyticsInputTypesRegistry>(
  eventType: T,
  data: AnalyticsInputTypesRegistry[T],
  isSentWhenPageIsHidden: boolean = false,
) => {
  const requestConfig: AxiosRequestConfig = isSentWhenPageIsHidden
    ? {
        adapter: 'fetch',
        fetchOptions: {
          keepalive: true,
        },
      }
    : {};
  await axios.post(
    `${getApiUrl('analytics-gateway', true)}/load-analytics-events`,
    buildRequestBody(eventType, data),
    requestConfig,
  );
};

/**
 * Posts over the public, unauthenticated route. It accepts AnonymousAppUserEventType only, and
 * rejects the whole batch on anything else.
 *
 * noAuth is required, not decorative: ilt-auth's request interceptor cancels any request it cannot
 * attach a token to, which is every request an anonymous visitor makes.
 */
export const postAnonymousAnalyticsEvent = async (
  data: AnonymousAppUserEvent,
  isSentWhenPageIsHidden: boolean = false,
) => {
  const requestConfig: AxiosRequestConfig = isSentWhenPageIsHidden
    ? { adapter: 'fetch', fetchOptions: { keepalive: true } }
    : {};

  await axios.post(
    `${getApiUrl('analytics-gateway', true)}/load-anonymous-analytics-events`,
    buildRequestBody('AnonymousAppUserEventType', data),
    { ...requestConfig, noAuth: true },
  );
};
